import {Badge, Stack, Tooltip, mergeSlotProps} from '@mui/material';
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  FilterPanelTrigger,
  GridColumnIcon,
  GridFilterListIcon,
  GridLogicOperator,
  Toolbar,
  ToolbarButton,
  gridClasses,
} from '@mui/x-data-grid-premium';
import PropTypes from 'prop-types';
import React from 'react';

const filterColumns = ({field, columns, currentFilters}) => {
  // remove already filtered fields from list of columns
  const filteredFields = currentFilters?.map(item => item.field);
  return columns
    .filter(
      colDef =>
        colDef.filterable &&
        (colDef.field === field || !filteredFields.includes(colDef.field))
    )
    .map(column => column.field);
};

const getColumnForNewFilter = ({currentFilters, columns}) => {
  const filteredFields = currentFilters?.map(({field}) => field);
  const columnForNewFilter = columns
    .filter(
      colDef => colDef.filterable && !filteredFields.includes(colDef.field)
    )
    .find(colDef => colDef.filterOperators?.length);
  return columnForNewFilter?.field ?? null;
};

const CustomToolbar = ({children}) => {
  return (
    <Toolbar sx={{justifyContent: 'space-between'}}>
      <Stack sx={{flexDirection: 'row'}}>
        <Tooltip title="Columns">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <GridColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
        <Tooltip title="Filters">
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton {...props} color="default">
                <Badge
                  badgeContent={state.filterCount}
                  color="primary"
                  variant="dot"
                >
                  <GridFilterListIcon
                    fontSize="small"
                    color={state.filterCount ? 'primary' : ''}
                  />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
      </Stack>
      {children}
    </Toolbar>
  );
};

CustomToolbar.propTypes = {
  children: PropTypes.node,
};

const DataGrid = ({slots, slotProps, sx, ...props}) => {
  return (
    <DataGridPremium
      disableColumnReorder
      disableDensitySelector
      disableMultipleColumnsSorting
      disableColumnSorting
      disableRowGrouping
      disableAggregation
      disablePivoting
      slots={mergeSlotProps(slots, {toolbar: CustomToolbar})}
      slotProps={mergeSlotProps(slotProps, {
        loadingOverlay: {
          'data-testid': 'loading-overlay',
        },
        toolbar: {
          showQuickFilter: false,
          csvOptions: {disableToolbarButton: true},
          printOptions: {disableToolbarButton: true},
          excelOptions: {disableToolbarButton: true},
        },
        filterPanel: {
          logicOperators: [GridLogicOperator.And], // Disable 'or'
          filterFormProps: {
            filterColumns,
          },
          getColumnForNewFilter,
        },
      })}
      sx={mergeSlotProps(sx, theme => ({
        '--DataGrid-containerBackground': (theme.vars || theme).palette
          .grey[100],
        ...theme.applyStyles('dark', {
          '--DataGrid-containerBackground': '#23343F',
        }),
        border: 'none',
        borderRadius: 0,
        '--DataGrid-rowBorderColor': (theme.vars || theme).palette.divider,
        '.MuiDataGrid-withBorderColor': {
          borderColor: (theme.vars || theme).palette.divider,
        },
        '& .small-padding--cell': {
          padding: 0,
          paddingLeft: 0.5,
        },
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
          outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
          {
            outline: 'none',
          },
        [`& .${gridClasses.columnHeaders}`]: {
          borderRadius: 0,
          backgroundImage: 'var(--Paper-overlay)',
        },
      }))}
      {...props}
    />
  );
};

DataGrid.propTypes = {
  slots: PropTypes.object,
  slotProps: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default DataGrid;
